import { Link } from "react-router-dom";
import {
  KeyRound,
  ShieldCheck,
  Webhook,
  AlertTriangle,
  BadgeCheck,
  ArrowUpRight,
  Copy,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * PaypalKeysGuide.jsx
 * ------------------------------------------------------------------
 * Step-by-step walkthrough for a merchant to find their PayPal
 * Client ID, Secret, and Webhook ID, so they can paste them into
 * their Checkout DM payment settings.
 *
 * Text-only guide — no screenshots. Includes an "account
 * requirements" callout up front (Business account + KYC), since
 * that trips people up before they ever reach the Developer
 * Dashboard. Webhook section links out to PayPal's own event-names
 * documentation (nofollow).
 * ------------------------------------------------------------------
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]" },
};

function StepCard({ n, accent, title, desc, note }) {
  const a = ACCENTS[accent];
  return (
    <div className="relative rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-10 w-10 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText} font-display text-sm font-black`}>
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-ink">{title}</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
          {note && <p className="mt-2 text-[12px] leading-relaxed text-ink/45">{note}</p>}
        </div>
      </div>
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
            {tagline}
          </p>
          <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
        </div>
      </div>
      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}
      <div className="mt-6 space-y-3">{children}</div>
    </section>
  );
}

export default function PaypalKeysGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Where to find your PayPal Client ID, Secret, and Webhook ID",
    description: "A step-by-step guide to locating your PayPal Client ID, Secret, and Webhook ID to connect to Checkout DM.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/paypal-keys-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "PayPal Keys Guide", item: "https://checkoutdm.com/paypal-keys-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Find your PayPal Client ID, Secret & Webhook ID | Checkout DM"
        description="Step-by-step guide to locating your PayPal Client ID, Secret, and Webhook ID so you can connect PayPal to Checkout DM."
        path="/paypal-keys-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help · PayPal</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Finding your PayPal keys
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          You'll need three things from the PayPal Developer Dashboard to connect PayPal
          to Checkout DM: your <strong>Client ID</strong>, your <strong>Secret</strong>, and
          your <strong>Webhook ID</strong>. This walks through exactly where each one lives —
          plus what needs to be true about your PayPal account before you get there.
        </p>

        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-hotpink" />
          <p className="text-[13px] leading-relaxed text-ink/70">
            Your Secret is sensitive — treat it like a password. Never paste it anywhere
            other than your Checkout DM Payment Settings.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* Account requirements */}
          <GuideSection
            id="account-requirements"
            icon={BadgeCheck}
            accent="peach"
            name="Before you start"
            tagline="Account requirements"
            what="The Developer Dashboard itself is open to anyone with a PayPal login, but actually receiving live payments through it depends on the state of your regular PayPal account."
          >
            <StepCard
              n={1}
              accent="peach"
              title="You need a PayPal Business account"
              desc="A personal PayPal account can log into the Developer Dashboard and create Sandbox credentials for testing, but it can't accept live payments through the API. To go live, your account has to be a Business account."
              note="If yours is currently personal, log in at paypal.com and look for the option to upgrade to a Business account — this is done in your regular PayPal account settings, not in the Developer Dashboard."
            />
            <StepCard
              n={2}
              accent="peach"
              title="Complete identity verification (KYC) if PayPal asks for it"
              desc="Depending on your country, PayPal may require you to verify your identity and business details — sometimes called KYC — before you can receive live payments. If this applies to you, PayPal will usually show a 'Verify your identity' prompt inside your regular account, asking for documents like a government ID and proof of business."
              note="This step, and exactly what's required, varies by country and is set by PayPal, not by Checkout DM. If you don't see a verification prompt, it likely isn't required for your account yet — but PayPal can still ask for it later before releasing a payout."
            />
            <StepCard
              n={3}
              accent="peach"
              title="Once verified, head to the Developer Dashboard"
              desc="With a verified Business account in place, you're ready to generate your Client ID, Secret, and Webhook ID using the steps below. Sandbox credentials, for testing, don't require any of this and can be created right away."
            />
          </GuideSection>

          {/* API Keys */}
          <GuideSection
            id="api-keys"
            icon={KeyRound}
            accent="lilac"
            name="Client ID & Secret"
            tagline="Step 1"
            what="These two values authenticate every payment request Checkout DM sends on your behalf, through a PayPal 'app' tied to your business account."
          >
            <StepCard
              n={1}
              accent="lilac"
              title="Log in to the PayPal Developer Dashboard"
              desc="Go to developer.paypal.com and log in using your regular PayPal account credentials — the same login as your Business account."
            />
            <StepCard
              n={2}
              accent="lilac"
              title="Go to Apps & Credentials"
              desc="From the Dashboard menu, click Apps & Credentials."
            />
            <StepCard
              n={3}
              accent="lilac"
              title="Pick Sandbox or Live"
              desc="Use the toggle near the top of the page to switch between Sandbox (for testing with fake payments) and Live (for real customer payments)."
              note="Sandbox and Live each have their own separate app, Client ID, Secret, and Webhook ID — don't mix them up. Live will only work once your account meets the requirements above."
            />
            <StepCard
              n={4}
              accent="lilac"
              title="Create or open an app"
              desc="If you don't have an app yet, click Create App, give it a name, and create it. If you already have one, click into it from the list."
            />
            <StepCard
              n={5}
              accent="lilac"
              title="Copy the Client ID and Secret"
              desc="On the app's detail page, the Client ID is shown in full. Click Show next to Secret to reveal it, then copy both."
              note="You can come back to this page and reveal the Secret again any time — it isn't a one-time reveal like some providers."
            />
          </GuideSection>

          {/* Webhook */}
          <GuideSection
            id="webhook"
            icon={Webhook}
            accent="mint"
            name="Webhook ID"
            tagline="Step 2"
            what="The webhook is how PayPal tells Checkout DM the moment a payment completes. The Webhook ID is what Checkout DM uses to verify that a webhook really came from PayPal."
          >
            <StepCard
              n={1}
              accent="mint"
              title="Scroll to the Webhooks section"
              desc="Still on your app's detail page, scroll down to the Webhooks section, then click Add Webhook."
            />

            <div className="rounded-2xl border border-mint/40 bg-mint/10 p-5">
              <p className="text-sm font-bold text-ink">Your Checkout DM webhook URL</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">
                Paste this exact URL into the URL field in PayPal — it's the same for every
                merchant, so you don't need to look it up anywhere else in your dashboard.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3">
                <code className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                  https://n8ncdm.checkoutdm.com/webhook/checkoutdm-paypal-webhook
                </code>
                <Copy size={15} className="flex-none text-ink/40" />
              </div>
            </div>

            <StepCard
              n={2}
              accent="mint"
              title="Paste in the webhook URL"
              desc="Paste the URL above into the URL field."
            />
            <StepCard
              n={3}
              accent="mint"
              title="Choose which events to send"
              desc="Below the URL field is a checklist of event types. This is where you tell PayPal which payment events should actually notify Checkout DM."
              note="At minimum, Checkout DM needs PAYMENT.CAPTURE.COMPLETED — that's the event that marks an order as paid. It's also worth ticking PAYMENT.CAPTURE.DENIED and PAYMENT.CAPTURE.REFUNDED if you see them, so failed or refunded payments are reflected too."
            />
            <StepCard
              n={4}
              accent="mint"
              title="Copy the Webhook ID"
              desc="Once saved, the webhook appears in the Webhooks list with its own Webhook ID shown directly beneath it — copy that value."
            />

            <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
              <p className="text-sm font-bold text-ink">What each event actually means</p>
              <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-ink/70">
                <li>
                  <span className="font-semibold text-ink">PAYMENT.CAPTURE.COMPLETED</span> — the
                  payment was successfully captured. This is the main event Checkout DM uses to
                  mark an order as paid.
                </li>
                <li>
                  <span className="font-semibold text-ink">PAYMENT.CAPTURE.DENIED</span> — the
                  capture attempt was declined. Checkout DM can use this to let the customer know
                  and, where possible, prompt them to retry.
                </li>
                <li>
                  <span className="font-semibold text-ink">PAYMENT.CAPTURE.REFUNDED</span> — a
                  previously captured payment was refunded, in full or in part.
                </li>
              </ul>
              <p className="mt-4 text-[13px] leading-relaxed text-ink/70">
                PayPal maintains the full, always-up-to-date list of webhook event names — across
                payments, disputes, subscriptions, and more — in their own documentation. Worth a
                look if you want to go beyond what Checkout DM currently listens for.
              </p>
              <a
                href="https://developer.paypal.com/api/rest/webhooks/event-names"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-hotpink hover:underline"
              >
                PayPal's webhook event names documentation
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </GuideSection>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <ShieldCheck size={17} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Where these go</p>
              <p className="mt-1 max-w-md text-sm text-ink/70">
                Paste your Client ID, Secret, and Webhook ID into the PayPal section of your
                Payment Settings in Checkout DM. Nothing is charged until this is saved and verified.
              </p>
            </div>
          </div>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
